import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../../cn';

type CopyButtonProps = {
  value: string;
  label?: string;
  successMessage?: string;
  className?: string;
};

const COPIED_FEEDBACK_MS = 1500;

export function CopyButton({ value, label, successMessage, className }: CopyButtonProps) {
  const { t, notify } = useResourceKit();
  const [copied, setCopied] = useState(false);
  const revertTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyLabel = label ?? t('rk.ui.copy');
  const copiedMessage = successMessage ?? t('rk.ui.copied');

  useEffect(
    () => () => {
      if (revertTimer.current) clearTimeout(revertTimer.current);
    },
    [],
  );

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (revertTimer.current) clearTimeout(revertTimer.current);
      revertTimer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
      notify('success', copiedMessage);
    } catch {
      notify('error', t('rk.ui.copyFailed'));
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleCopy}
        className={cn(
          'p-1 text-muted-foreground/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm transition-colors',
          className,
        )}
        aria-label={copyLabel}
      >
        {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
      </button>
      {copied && (
        <span role="status" className="sr-only">
          {copiedMessage}
        </span>
      )}
    </>
  );
}
